import type { RepresentativeModel } from '@/app/types/representative.type.ts';
import { type UserModel, UserTypeEnum } from '@/app/types/user-type.ts';
import { isPostHogConfigured, posthog, userTypeLabel } from './posthog.ts';

function isRepresentative(user: UserModel): user is RepresentativeModel {
  return user.userType === UserTypeEnum.DrugRep;
}

export function buildPersonProperties(user: UserModel) {
  const typeLabel = userTypeLabel(user.userType);
  const properties: Record<string, string | number | boolean | undefined> = {
    email: user.emailAddress,
    name: `${user.firstName} ${user.lastName}`.trim(),
    first_name: user.firstName,
    last_name: user.lastName,
    user_type: typeLabel,
    user_id: user.id,
    receive_chats: user.receiveChats,
    agree_email: user.agreeEmail,
    agree_text: user.agreeText,
  };

  if (isRepresentative(user)) {
    properties.company = user.company;
    properties.subscription_status = user.subscriptionStatus;
    properties.has_active_subscription = user.hasActiveSubscription;
    properties.subscription_source = user.subscriptionSource;
    properties.subscription_management_source = user.subscriptionManagementSource;
    properties.has_store_subscription = user.hasStoreSubscription;
    properties.has_web_subscription = user.hasWebSubscription;
  }

  return properties;
}

export function buildSuperProperties(user: UserModel | null, isImpersonating: boolean) {
  const properties: Record<string, string | number | boolean | undefined | null> = {
    app_surface: 'web',
    environment: import.meta.env.MODE,
    is_impersonating: isImpersonating,
  };

  if (user) {
    properties.user_type = userTypeLabel(user.userType);
    if (isImpersonating) {
      properties.impersonated_user_key = user.key;
    }
    if (isRepresentative(user)) {
      properties.subscription_status = user.subscriptionStatus;
      properties.has_active_subscription = user.hasActiveSubscription;
    }
  }

  return properties;
}

export function identifyAnalyticsUser(user: UserModel, isImpersonating: boolean) {
  if (!isPostHogConfigured || isImpersonating) {
    registerAnalyticsSuperProperties(user, isImpersonating);
    return;
  }

  posthog.identify(user.key, buildPersonProperties(user));
  registerAnalyticsSuperProperties(user, isImpersonating);
}

let registeredUserType: string | undefined;

export function getRegisteredUserType() {
  return registeredUserType;
}

export function registerAnalyticsSuperProperties(user: UserModel | null, isImpersonating: boolean) {
  if (!isPostHogConfigured) {
    return;
  }

  registeredUserType = user ? userTypeLabel(user.userType) : undefined;
  posthog.register(buildSuperProperties(user, isImpersonating));
}

export function subscriptionSnapshot(user: UserModel | null) {
  if (!user || !isRepresentative(user)) {
    return null;
  }

  return {
    subscriptionStatus: user.subscriptionStatus,
    hasActiveSubscription: user.hasActiveSubscription,
  };
}

export function subscriptionChanged(
  previous: ReturnType<typeof subscriptionSnapshot>,
  user: UserModel | null,
) {
  const next = subscriptionSnapshot(user);
  if (!next) {
    return false;
  }

  return (
    previous?.subscriptionStatus !== next.subscriptionStatus ||
    previous?.hasActiveSubscription !== next.hasActiveSubscription
  );
}
